import React, {useRef, useState} from 'react'
import {Button} from "primereact/button";
import PublicTournamentTreeTab from "../tournaments/PublicTournamentTreeTab";
import SpielDetailsDialog from "./SpielDetailsDialog";
import ZoomSteuerung, {useBaumZoom, useLiveZoom, ZOOM_STUFEN} from "./ZoomSteuerung";
import LiveView from "../liveview/LiveView";
import {LiveDarstellungAuswahl, useLiveDarstellung} from "./LiveBereich";
import {getFromLocalStorageWithDefault, saveToLocalStorage} from "../../../../util/localStorageZugriff";
import {useVollbild} from "./browserVollbild";
import {LeisteAusblendenKnopf, LeisteEinblendenKnopf, useLeisteAusblendbar} from "./VollbildLeisteKnoepfe";

const KEY_LIVE_HOEHE = "dh_public_alle_baeume_live_hoehe";
// Höhe der Live-Ansicht in Prozent des Vollbilds
const LIVE_HOEHE_START = 45;
const LIVE_HOEHE_MIN = 15;
const LIVE_HOEHE_MAX = 85;
// so viel Platz (px) bleibt für die Bäume bzw. die Live-Ansicht mindestens
const MIN_BEREICH_PX = 80;

const begrenzeLiveHoehe =(wert) => Math.min(LIVE_HOEHE_MAX, Math.max(LIVE_HOEHE_MIN, wert));

const ladeLiveHoehe = () => {
    try {
        const gespeichert = getFromLocalStorageWithDefault(KEY_LIVE_HOEHE, undefined);
        return typeof gespeichert === "number" && !isNaN(gespeichert) ? begrenzeLiveHoehe(gespeichert) : LIVE_HOEHE_START;
    } catch (e) {
        // ohne Speicher (z.B. privater Modus) gilt der Startwert
        return LIVE_HOEHE_START;
    }
}

const speichereLiveHoehe = (wert) => {
    try {
        saveToLocalStorage(KEY_LIVE_HOEHE, wert);
    } catch (e) {
        // ohne Speicher (z.B. privater Modus) gilt die Höhe nur bis zum Neuladen
    }
}


/**
 * Alle KO-Bäume eines Events (Gold, Silber, Bronze) untereinander, mit gemeinsamem Vollbildmodus.
 * Jeder Baum hat seinen eigenen Zoom, in der normalen Ansicht und im Vollbild.
 * Im Vollbild nebeneinander kann unten zusätzlich die Live-Ansicht eingeblendet werden.
 *
 * @param baeume Liste von {key, farbe, tournament, model, boardReihenfolge, beschreibungen}
 */
export default function AlleBaeumeBereich({baeume, boardGames, liveGames, tournamentId, eventOperatorId, appCall}) {

    // Vollbild über die ganze Seite, außerhalb der App auch das echte Vollbild des Browsers
    const [vollbild, starteVollbild, schliesseVollbild] = useVollbild(appCall);
    // Zoom je Baum (key -> Index), gilt für die normale Ansicht und das Vollbild, auf Handys startet er bei 75 %
    const [startIndex] = useBaumZoom();
    const [baumZoom, setBaumZoom] = useState({});
    const getBaumZoom = (key) => baumZoom[key] !== undefined ? baumZoom[key] : startIndex;
    const setzeBaumZoom = (key) => (aenderung) => setBaumZoom(alt => {
        const bisher = alt[key] !== undefined ? alt[key] : startIndex;
        return {...alt, [key]: typeof aenderung === "function" ? aenderung(bisher) : aenderung};
    });
    // nur im Vollbild: Bäume untereinander oder nebeneinander
    const [nebeneinander, setNebeneinander] = useState(false);
    // nur im Vollbild nebeneinander: Live-Ansicht unten einblenden, Darstellung und Zoom wie im Live-Bereich (im Browser gemerkt)
    const [mitLive, setMitLive] = useState(false);
    const [liveDarstellung, waehleLiveDarstellung] = useLiveDarstellung(appCall);
    const [liveZoomIndex, setzeLiveZoom] = useLiveZoom(appCall);
    const [zahnradZiel, setZahnradZiel] = useState(null);
    const liveAnzeigen = nebeneinander && mitLive && tournamentId !== undefined && tournamentId !== null;
    // im Vollbild: Leiste der Bäume und Leiste der Live-Ansicht ausblendbar, damit mehr Platz bleibt
    const [baumLeisteAus, setBaumLeisteAus] = useLeisteAusblendbar(vollbild);
    const [liveLeisteAus, setLiveLeisteAus] = useLeisteAusblendbar(vollbild);
    // Höhe der Live-Ansicht, über den Trenner verschiebbar, wird im Browser gemerkt
    const [liveHoehe, setLiveHoehe] = useState(ladeLiveHoehe);
    const vollbildRef = useRef(null);
    const inhaltRef = useRef(null);
    const ziehen = useRef(false);

    const berechneLiveHoehe = (clientY) => {
        const vollbildElement = vollbildRef.current;
        const inhaltElement = inhaltRef.current;
        if (!vollbildElement || !inhaltElement) {
            return undefined;
        }
        const rahmen = vollbildElement.getBoundingClientRect();
        if (rahmen.height <= 0) {
            return undefined;
        }
        // oben bleibt Platz für die Bäume (unterhalb der Kopfleiste), unten für die Live-Ansicht
        const oben = inhaltElement.getBoundingClientRect().top + MIN_BEREICH_PX;
        const unten = rahmen.bottom - MIN_BEREICH_PX;
        const y = Math.min(unten, Math.max(oben, clientY));
        return begrenzeLiveHoehe((rahmen.bottom - y) / rahmen.height * 100);
    }

    const trennerPointerDown = (event) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        ziehen.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
    }

    const trennerPointerMove = (event) => {
        if (!ziehen.current) {
            return;
        }
        const neu = berechneLiveHoehe(event.clientY);
        if (neu !== undefined) {
            setLiveHoehe(neu);
        }
    }

    const trennerPointerUp = (event) => {
        if (!ziehen.current) {
            return;
        }
        ziehen.current = false;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setLiveHoehe(alt => {
            speichereLiveHoehe(alt);
            return alt;
        });
    }

    // per Tastatur: Pfeil hoch/runter verschiebt in 5-%-Schritten, Pos1/Ende auf Maximum/Minimum
    const trennerKeyDown = (event) => {
        let neu;
        if (event.key === "ArrowUp") {
            neu = liveHoehe + 5;
        } else if (event.key === "ArrowDown") {
            neu = liveHoehe - 5;
        } else if (event.key === "Home") {
            neu = LIVE_HOEHE_MAX;
        } else if (event.key === "End") {
            neu = LIVE_HOEHE_MIN;
        } else {
            return;
        }
        event.preventDefault();
        neu = begrenzeLiveHoehe(neu);
        setLiveHoehe(neu);
        speichereLiveHoehe(neu);
    }

    const getBaeume = (mitScrollbereich, alsSpalten) => {
        return <div className={alsSpalten ? "dhpt-alle-baeume dhpt-alle-baeume-nebeneinander" : "dhpt-alle-baeume"}>
            {baeume.map(baum => <section key={baum.key} className="dhpt-baum-abschnitt">
                <div className="dhpt-baum-kopf">
                    <h2 className="dhpt-baum-titel">
                        {baum.farbe && <i className={"dhpt-punkt dhpt-punkt-" + baum.farbe} aria-hidden="true"/>}
                        {baum.tournament.bezeichnung}
                    </h2>
                    {/* im Vollbild steht der Zoom je Baum in der Kopfleiste */}
                    {mitScrollbereich && <span className="dhpt-baum-zoom">
                        <ZoomSteuerung zoomIndex={getBaumZoom(baum.key)} setZoomIndex={setzeBaumZoom(baum.key)} klein={true}/>
                    </span>}
                </div>
                <div className={mitScrollbereich ? "dhpt-baum" : undefined}>
                    {/* jeder Baum zoomt für sich, in der normalen Ansicht in seinem Scrollbereich, im Vollbild scrollt der ganze Inhalt */}
                    <div style={{zoom: ZOOM_STUFEN[getBaumZoom(baum.key)]}}>
                        <PublicTournamentTreeTab tournament={baum.tournament} model={baum.model}
                                                 boardGames={boardGames} liveGames={liveGames}
                                                 boardReihenfolge={baum.boardReihenfolge}
                                                 tournamentBeschreibungen={baum.beschreibungen}
                                                 spielDetailsDialog={SpielDetailsDialog}/>
                    </div>
                </div>
            </section>)}
        </div>
    }

    if (vollbild) {
        return <div ref={vollbildRef} className={"dhpt-vollbild" + (baumLeisteAus ? " dhpt-vollbild-ohne-leiste" : "")}
                    role="dialog" aria-modal="true" aria-label="Alle Turnierbäume im Vollbild">
            {/* ausgeblendete Leiste: nur ein kleiner Knopf oben rechts zum Einblenden */}
            {baumLeisteAus && <LeisteEinblendenKnopf label="Menü einblenden" oben={true} onClick={() => setBaumLeisteAus(false)}/>}
            <div className={"dhpt-vollbild-leiste dhpt-vollbild-leiste-umbruch" + (baumLeisteAus ? " dhpt-ausgeblendet" : "")}>
                <span className="dhpt-vollbild-titel">{baeume.length === 2 ? "Beide Bäume" : "Alle Bäume"}</span>
                <div className="dhpt-segment" role="radiogroup" aria-label="Anordnung der Bäume">
                    <button type="button" role="radio" aria-checked={!nebeneinander} className="dhpt-segment-knopf" title="Untereinander"
                            onClick={() => setNebeneinander(false)}>
                        <i className="pi pi-bars" aria-hidden="true"/><span className="dhpt-segment-label">Untereinander</span>
                    </button>
                    <button type="button" role="radio" aria-checked={nebeneinander} className="dhpt-segment-knopf" title="Nebeneinander"
                            onClick={() => setNebeneinander(true)}>
                        <i className="pi pi-objects-column" aria-hidden="true"/><span className="dhpt-segment-label">Nebeneinander</span>
                    </button>
                </div>
                {/* nur nebeneinander: Live-Ansicht unten ein-/ausblenden */}
                {nebeneinander && tournamentId !== undefined && tournamentId !== null &&
                    <Button icon="pi pi-video" label={mitLive ? "Live ausblenden" : "Live anzeigen"} aria-pressed={mitLive}
                            className={"p-button-sm" + (mitLive ? "" : " p-button-outlined dhpt-button-hell")}
                            onClick={() => setMitLive(!mitLive)}/>}
                {/* Zoom je Baum, statt einer Beschriftung steht der Punkt in der Farbe des Baums davor */}
                {baeume.map(baum => <span key={baum.key} className="dhpt-vollbild-baum-zoom" role="group" aria-label={"Zoom " + baum.tournament.bezeichnung}
                                          title={baum.tournament.bezeichnung}>
                    <i className={"dhpt-punkt dhpt-punkt-" + (baum.farbe || baum.key)} aria-hidden="true"/>
                    <ZoomSteuerung zoomIndex={getBaumZoom(baum.key)} setZoomIndex={setzeBaumZoom(baum.key)}/>
                </span>)}
                <LeisteAusblendenKnopf onClick={() => setBaumLeisteAus(true)}/>
                <Button icon="pi pi-times" label="Schließen" className="p-button-secondary" onClick={schliesseVollbild}/>
            </div>
            <div ref={inhaltRef} className="dhpt-vollbild-inhalt">
                {/* gescrollt wird im Vollbild als Ganzes, gezoomt je Baum */}
                {getBaeume(false, nebeneinander)}
            </div>
            {/* Trenner zum Verschieben der Höhe zwischen Bäumen und Live-Ansicht */}
            {liveAnzeigen && <div className="dhpt-vollbild-trenner" role="separator" aria-orientation="horizontal"
                                  aria-label="Höhe der Live-Ansicht verschieben" tabIndex={0}
                                  aria-valuemin={LIVE_HOEHE_MIN} aria-valuemax={LIVE_HOEHE_MAX} aria-valuenow={Math.round(liveHoehe)}
                                  title="Ziehen, um die Höhe zu ändern"
                                  onPointerDown={trennerPointerDown} onPointerMove={trennerPointerMove}
                                  onPointerUp={trennerPointerUp} onPointerCancel={trennerPointerUp}
                                  onKeyDown={trennerKeyDown}>
                <span className="dhpt-vollbild-trenner-griff" aria-hidden="true"/>
            </div>}
            {liveAnzeigen && <div className="dhpt-vollbild-live" style={{flexBasis: liveHoehe + "%"}}>
                {liveLeisteAus && <LeisteEinblendenKnopf label="Live-Menü einblenden" onClick={() => setLiveLeisteAus(false)}/>}
                {/* nur per CSS ausgeblendet, damit der Platz für das Zahnrad der TV-Ansichten erhalten bleibt */}
                <div className={"dhpt-vollbild-live-leiste" + (liveLeisteAus ? " dhpt-ausgeblendet" : "")}>
                    <span className="dhpt-chip dhpt-chip-live">Live</span>
                    <LiveDarstellungAuswahl appCall={appCall} darstellung={liveDarstellung} waehleDarstellung={waehleLiveDarstellung}/>
                    <span className="dhpt-live-zoom">
                        <span ref={setZahnradZiel} className="dhpt-zahnrad-ziel"/>
                        <ZoomSteuerung zoomIndex={liveZoomIndex} setZoomIndex={setzeLiveZoom} klein={true}/>
                        <LeisteAusblendenKnopf label="Live-Menü ausblenden" onClick={() => setLiveLeisteAus(true)}/>
                    </span>
                </div>
                <div className="dhpt-vollbild-live-inhalt">
                    <div style={{zoom: ZOOM_STUFEN[liveZoomIndex]}}>
                        <LiveView appCall={appCall === true} tournamentid={tournamentId} eventOperatorId={eventOperatorId} showEmbedded={true}
                                  zahnradZiel={zahnradZiel} viewModusVorgabe={liveDarstellung} ohneAnsichtAuswahl={true}/>
                    </div>
                </div>
            </div>}
        </div>
    }

    return <div>
        <div className="dhpt-baum-leiste">
            <Button icon="pi pi-window-maximize" label="Vollbild" className="p-button-sm p-button-outlined dhpt-button-hell" onClick={starteVollbild}/>
        </div>
        {getBaeume(true, false)}
    </div>
}
